import { floatify } from 'lib/maths/mathUtils'
import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/featuresSelectors'
import { getLuxPlusSubscriptionItems } from '../view/luxPlusSubscription'
import getPayableAfterModifiers from './getPayableAfterModifiers'
import getDepositCalculations from './deposit/getDepositCalculations'
import getGiftCardPayAmount from './getGiftCardPayAmount'
import getCheckoutTotals from './getCheckoutTotals'
import { getInsuranceTotals } from './insurance'
import { getBookingProtectionTotals } from './bookingProtection'
import { PAYMENT_OPTIONS, PAYMENT_SCHEDULE_STATUS } from 'constants/payment'
import getGroupPaymentLeadContributionAmount from './groupPayment/getGroupPaymentLeadContributionAmount'

const getCreditPayableAmount = createSelector(
  (state: App.State) => getPayableAfterModifiers(state),
  (state: App.State) => state.auth.account.creditsByCurrency[state.checkout.cart.currencyCode],
  (state: App.State) => isSpoofed(state),
  (state: App.State) => getDepositCalculations(state),
  (state: App.State) => state.checkout.payment.rebookingID,
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (state: App.State) => getGiftCardPayAmount(state),
  (state: App.State) => state.checkout.payment.useGiftCard,
  (state: App.State) => state.checkout.cart.mode,
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getCheckoutTotals(state),
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getBookingProtectionTotals(state),
  (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.GROUP_PAYMENT,
  (state: App.State) => getGroupPaymentLeadContributionAmount(state),
  (
    payable,
    credits,
    isSpoofed,
    depositCalculations,
    rebookingID,
    luxPlusCartSubscriptionItems,
    giftCardPayAmount,
    useGiftCard,
    cartMode,
    existingOrder,
    checkoutTotals,
    insuranceTotals,
    bookingProtectionTotals,
    isGroupPayment,
    groupPaymentLeadContributionAmount,
  ): number => {
    let maxCreditTotal: number
    const creditBalance = credits?.balance ?? 0

    // Check if this is a change-dates flow with an active payment schedule
    // In this case, credit should be limited to the "pay now" amount, not the full adjustment
    const hasActivePaymentSchedule = existingOrder?.paymentScheduleDetails &&
      existingOrder.paymentScheduleDetails.status === PAYMENT_SCHEDULE_STATUS.ACTIVE

    let effectivePayable = payable

    if ((cartMode === 'change-dates' || cartMode === 'change-package') && hasActivePaymentSchedule) {
      // Use the paymentScheduleAdjustment (pay now amount) plus any insurance/booking protection
      // This mirrors the logic in getPaymentAmount in CheckoutPurchaseBox
      const paymentScheduleAdjustment = (checkoutTotals.data.paymentScheduleAdjustment ?? 0) +
        (insuranceTotals?.data.price ?? 0) +
        (bookingProtectionTotals?.data.price ?? 0)

      if (paymentScheduleAdjustment > 0) {
        effectivePayable = paymentScheduleAdjustment
      }
    }

    if (isGroupPayment && groupPaymentLeadContributionAmount > 0) {
      effectivePayable = groupPaymentLeadContributionAmount
    }

    if (isSpoofed && rebookingID) {
      maxCreditTotal = depositCalculations.maxDepositCreditAmount
    } else {
      maxCreditTotal = Math.min(creditBalance, effectivePayable)
    }

    if (luxPlusCartSubscriptionItems.length > 0) {
      const subscriptionItem = luxPlusCartSubscriptionItems[0]
      const maxCreditTotalPayableForSubscription = floatify(effectivePayable - subscriptionItem.amount)

      if (maxCreditTotal > maxCreditTotalPayableForSubscription) {
        maxCreditTotal = maxCreditTotalPayableForSubscription
      }
    }

    // Use deposit gift card amount if available for rebooking scenarios
    let giftCardAmount = giftCardPayAmount
    if (useGiftCard && isSpoofed && rebookingID) {
      giftCardAmount = depositCalculations.maxDepositGiftCardAmount
    }

    maxCreditTotal = Math.min(maxCreditTotal, floatify(effectivePayable - giftCardAmount))

    return floatify(maxCreditTotal)
  },
)

export default getCreditPayableAmount
